import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { getCustomerQueryOptions } from '@/features/customers/query-options'

/**
 * 荷主 (Customer) 選択時に、その荷主のデフォルト付帯費用カテゴリを
 * 付帯費用の入力枠へ自動投入するための hook (#1857 指摘-4)。
 *
 * - `customerId` が変わるたびに荷主単体を fetch し
 *   `defaultAdditionalPaymentCategories` を取得する
 * - 取得データが現在の `customerId` と一致したら `onApply(categories)` を 1 回呼ぶ
 *   (実際の field 配列操作は呼び出し側に委ねる。請求側のみ / 請求+支払 両方など
 *    モーダルごとに投入先が異なるため)
 *   VOC_PJ-865 #3819: 投入行の運賃込み初期値をマスタ駆動にするため、id だけでなく
 *   各カテゴリの `isFareIncludedByDefault` も併せて渡す。
 * - 同一 `customerId` への重複適用は `lastAppliedRef` でガードする
 *   (`onApply` が未メモ化でも再適用しない)
 *
 * `enabled=false` のときは fetch も適用も行わない。編集系の画面では既に付帯費用が
 * 入った状態でフォームが初期化されるため、荷主 default の投入（＝完全な置き換え）が
 * 走ると既存行を消してしまう。fetch を止めるだけでは防げない——一覧など別画面が
 * 同じ荷主を取得済みだと TanStack Query のキャッシュから `data` が返り、適用側の
 * 条件が成立するため（#5146 実機で発覚）。
 */
export const useCustomerDefaultAdditionals = ({
  tenantKey,
  customerId,
  enabled = true,
  onApply
}: {
  tenantKey: string
  customerId: number | null | undefined
  enabled?: boolean
  onApply: (
    categories: {
      id: number
      isFareIncludedByDefault: boolean
      note: string | null
    }[]
  ) => void
}): void => {
  const { data } = useQuery({
    ...getCustomerQueryOptions({ tenantKey, customerId: customerId ?? 0 }),
    enabled: enabled && customerId != null,
    retry: false
  })

  const lastAppliedRef = useRef<number | null>(null)

  useEffect(() => {
    // fetch を止めるだけでは足りない（キャッシュ由来の data で適用が走るため）
    if (!enabled) return
    if (customerId == null) {
      // 荷主クリア時は次回選択で再適用できるようリセット
      lastAppliedRef.current = null
      return
    }
    if (lastAppliedRef.current === customerId) return
    // fetch 中 / 古い荷主のデータの間は待つ
    if (!data || data.id !== customerId) return

    lastAppliedRef.current = customerId
    // defaultAdditionalPaymentCategories は #1857 で追加されたフィールド。
    // 旧 BE では欠落しうるため ?? [] でガードする (undefined.map クラッシュ防止)。
    // VOC_PJ-865 #3819: 各カテゴリの isFareIncludedByDefault も渡す。旧 BE で
    // 当該フィールドが欠落するレスポンスでも落ちないよう ?? false で補完する。
    onApply(
      (data.defaultAdditionalPaymentCategories ?? []).map((c) => ({
        id: c.id,
        isFareIncludedByDefault: c.isFareIncludedByDefault ?? false,
        // VOC_PJ-1000 #5264: 投入行の備考に費目マスタの初期値を写すため渡す
        note: c.note ?? null
      }))
    )
  }, [enabled, customerId, data, onApply])
}
